import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import { Trans } from "@lingui/react/macro";
import { BASE_URL } from "../../const";

interface Company {
  tenant_id: string;
  name: string;
  created_at: string;
}

/**
 * Decode the current session JWT to extract the tenant_id.
 * Login-as partner usernames follow: partner+{tenant_id}@partner.asktally.com
 */
function getCurrentTenantId(): string | null {
  const token = localStorage.getItem("token");
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const sub: string = payload.sub ?? "";
    const match = sub.match(/^partner\+(.+)@partner\.asktally\.com$/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
}

/**
 * Create a standalone axios instance for partner API calls.
 * This avoids the global response interceptor which dispatches logout() on 401.
 */
function createPartnerAxios() {
  const partnerToken = localStorage.getItem("partnerToken");
  return axios.create({
    baseURL: BASE_URL,
    headers: {
      "Content-Type": "application/json",
      ...(partnerToken ? { Authorization: `Bearer ${partnerToken}` } : {}),
    },
  });
}

interface PartnerCompanySwitcherProps {
  closeDrawer: () => void;
}

const PartnerCompanySwitcher: React.FC<PartnerCompanySwitcherProps> = ({
  closeDrawer,
}) => {
  const theme = useTheme();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [visible, setVisible] = useState(true);

  const currentTenantId = getCurrentTenantId();

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const partnerAxios = createPartnerAxios();
        const response = await partnerAxios.get("/v1/partner/companies/");
        setCompanies(response.data ?? []);
      } catch (error: any) {
        if (error?.response?.status === 401) {
          // Partner token expired — clean up and hide switcher
          localStorage.removeItem("partnerToken");
          setVisible(false);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
  }, []);

  const handleSwitch = async (tenantId: string) => {
    if (tenantId === currentTenantId || switching) return;
    setSwitching(true);
    try {
      const partnerAxios = createPartnerAxios();
      const response = await partnerAxios.get(
        `/v1/partner/companies/${tenantId}/login-as/`,
      );
      const url: string = response.data?.url;
      if (url) {
        const newToken = new URL(url).searchParams.get("token");
        if (newToken) {
          localStorage.setItem("token", newToken);
          window.location.reload();
          return;
        }
      }
    } catch {
      // Silently fail — user stays on current company
    }
    setSwitching(false);
  };

  const handleBackToPortal = () => {
    const partnerToken = localStorage.getItem("partnerToken");
    if (partnerToken) {
      localStorage.setItem("token", partnerToken);
      localStorage.removeItem("partnerToken");
    }
    closeDrawer();
    window.location.href = "/partner";
  };

  if (!visible) return null;

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1} py="6px">
        <CircularProgress size={16} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h4"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(1),
          fontSize: "1rem !important",
          width: "100%",
          lineHeight: "1",
          margin: "0",
          py: "6px",
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
          }}
        >
          <SwapHorizIcon sx={{ fontSize: 20 }} />
        </Box>
        <Trans context="Drawer label for partner company switcher">
          Company
        </Trans>
      </Typography>

      <Select
        value={currentTenantId ?? ""}
        onChange={(e) => handleSwitch(e.target.value as string)}
        size="small"
        fullWidth
        disabled={switching}
        sx={{
          mt: 0.5,
          mb: 1,
          fontSize: "0.85rem",
          backgroundColor: theme.palette.action.hover,
          borderRadius: "8px",
          "& .MuiSelect-select": {
            py: "6px",
          },
        }}
      >
        {companies.map((company) => (
          <MenuItem key={company.tenant_id} value={company.tenant_id}>
            {company.name}
          </MenuItem>
        ))}
      </Select>

      <Typography
        variant="h4"
        onClick={handleBackToPortal}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: theme.spacing(1),
          cursor: "pointer",
          whiteSpace: "nowrap",
          fontSize: "1rem !important",
          width: "100%",
          lineHeight: "1",
          margin: "0",
          py: "6px",
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
            borderRadius: "25px",
          },
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "20px",
            height: "20px",
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 20 }} />
        </Box>
        <Trans context="Drawer link to return to partner portal">
          Partner Portal
        </Trans>
      </Typography>
    </Box>
  );
};

export default PartnerCompanySwitcher;
